import type { Episode, HistoryEntry, MediaType, SeriesDetails, SkipSegment } from '@teaplus/shared';

export function watchPath(
  e: Pick<HistoryEntry, 'mediaId' | 'contentType' | 'seasonNumber' | 'episodeNumber'>,
) {
  return e.contentType === 'movie'
    ? `/watch/movie/${e.mediaId}`
    : `/watch/series/${e.mediaId}/${e.seasonNumber ?? 1}/${e.episodeNumber ?? 1}`;
}

export function watchPathFor(type: MediaType, mediaId: string, season?: number, episode?: number) {
  return watchPath({
    mediaId,
    contentType: type,
    seasonNumber: season ?? null,
    episodeNumber: episode ?? null,
  });
}

const CREDITS_LONGEST = 5 * 60;
const CREDITS_SHARE = 0.04;

/**
 * Where the end credits start, in seconds. IntroDB's time when it knows this episode. Otherwise an
 * estimate, as nothing says where a film's credits are: the last 4%, and 5 minutes at most.
 */
export function creditsStartAt(duration: number, segments?: SkipSegment[] | null) {
  const known = segments?.find((s) => s.kind === 'credits' && s.start > duration / 2);
  return known ? known.start : duration - Math.min(CREDITS_LONGEST, duration * CREDITS_SHARE);
}

/**
 * Whether this is the last episode a show will ever have: the show has ended or been cancelled and
 * nothing comes after it. A season finale, or the newest episode of a show still running, isn't.
 */
export function isLastEpisodeEver(
  series: Pick<SeriesDetails, 'status' | 'seasons'>,
  seasonEpisodes: Pick<Episode, 'episodeNumber'>[],
  season: number,
  episode: number,
) {
  if (series.status !== 'Ended' && series.status !== 'Canceled') return false;
  if (season < 1) return false; // Specials sit outside the story.
  if (seasonEpisodes.some((e) => e.episodeNumber > episode)) return false;
  return !series.seasons.some((s) => s.seasonNumber > season && s.episodeCount > 0);
}
